import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import { Route as CampaignsListRoute } from "routes/_base/_auth/campaigns/list/route"
import { CampaignStatusEnum, FlowEnum } from "schema"
import { CreateCampaignButton } from "./CreateCampaignButton"

type Search = ReturnType<typeof CampaignsListRoute.useSearch>
type CampaignsFilterBy = NonNullable<Search["filterBy"]>

export function CampaignsFilter() {
  const { t } = useTranslation()
  const search = CampaignsListRoute.useSearch()
  const navigate = CampaignsListRoute.useNavigate()
  const labels = useFilterBarLabels()

  function onChangeFilter(changes: Partial<CampaignsFilterBy>) {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        filterBy: { ...previous.filterBy, ...changes },
      }),
    })
  }

  function onClearFilters() {
    navigate({
      search: (previous) => ({ ...previous, page: 1, filterBy: undefined, query: undefined }),
    })
  }

  const fields: FilterBarField[] = [
    {
      key: "status",
      type: "multiSelect",
      label: t("campaigns.table.status"),
      value: search.filterBy?.status,
      onChange: (values) =>
        onChangeFilter({ status: pickValues(Object.values(CampaignStatusEnum), values) }),
      options: Object.values(CampaignStatusEnum).map((status) => ({
        value: status,
        label: t(`campaigns.statuses.${status.toLowerCase()}`),
      })),
    },
    {
      key: "flow",
      type: "multiSelect",
      label: t("campaigns.table.flow"),
      value: search.filterBy?.flow,
      onChange: (values) => onChangeFilter({ flow: pickValues(Object.values(FlowEnum), values) }),
      options: Object.values(FlowEnum).map((flow) => ({
        value: flow,
        label: t(`campaigns.configuration.setup.flow.${flow.toLowerCase()}.title`),
      })),
    },
    {
      key: "archived",
      type: "select",
      label: t("campaigns.archived"),
      value: search.filterBy?.archived ? "true" : undefined,
      onChange: (value) => onChangeFilter({ archived: value === "true" ? true : undefined }),
      options: [{ value: "true", label: t("campaigns.table.archivedYes") }],
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("campaigns.table.search")}
      trailing={<CreateCampaignButton />}
    />
  )
}

function pickValues<T extends string>(allowed: readonly T[], values: string[] | undefined) {
  const picked = allowed.filter((value) => values?.includes(value))
  return picked.length > 0 ? picked : undefined
}
